import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { Box, Card, CardContent, CardHeader, Grid, LinearProgress, LinearProgressProps, Typography, useTheme } from '@mui/material';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';

import { gridSpacing } from 'store/constant';

import { GenericCardProps } from 'types';

interface SideIconCardProps extends GenericCardProps {
    // bgcolor?: string;
    metrics?: any;
    name?: string;
}

// =============================|| SIDE ICON CARD ||============================= //

const SideIconCard = ({ metrics, name }: SideIconCardProps) => {
    // hooks
    const intl = useIntl();
    const theme = useTheme();

    const color = useMemo(() => {
        if (metrics && metrics.overallScore <= 34) {
            return theme.palette.error.main;
        }

        if (metrics && metrics.overallScore <= 80) {
            return theme.palette.warning.main;
        }

        return theme.palette.success.main;
    }, [metrics, theme.palette.error.main, theme.palette.success.main, theme.palette.warning.main]);

    const iconComponent = useMemo(() => {
        const iconProps = { sx: { fontSize: 100, color: '#a483ff' } };

        if (metrics?.overallScore <= 34) {
            return <SentimentVeryDissatisfiedIcon {...iconProps} />;
        }

        if (metrics.overallScore <= 80) {
            return <SentimentNeutralIcon {...iconProps} />;
        }

        return <SentimentSatisfiedAltIcon {...iconProps} />;
    }, [metrics.overallScore]);

    return (
        <Card sx={{ background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[50], height: 1 }}>
            <Grid container justifyContent="space-between" alignItems="stretch" sx={{ height: 1 }}>
                <Grid
                    item
                    xs={12}
                    sm={4}
                    sx={{ background: color, px: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', py: { xs: 3, sm: 0 } }}
                >
                    {iconComponent}
                </Grid>
                <Grid item xs={12} sm={8} sx={{ height: 1 }}>
                    <CardHeader title={`${intl.formatMessage({ id: 'store-status' })} - ${name}`} />
                    <CardContent
                        sx={{
                            pt: {
                                xs: 0
                            },
                            width: 1
                        }}
                    >
                        <Box sx={{ mb: { xs: 2, sm: 1, lg: 3 }, width: 1 }}>
                            <LinearProgressWithLabel
                                variant="determinate"
                                value={metrics?.overallScore}
                                color={
                                    // eslint-disable-next-line no-nested-ternary
                                    metrics?.overallScore <= 34
                                        ? 'error'
                                        : metrics.overallScore > 34 && metrics.overallScore <= 80
                                        ? 'warning'
                                        : 'success'
                                }
                            />
                        </Box>
                        <Grid
                            container
                            sx={{
                                width: 1,
                                flexDirection: {
                                    xs: 'column',
                                    sm: 'row-reverse'
                                },
                                alignItems: 'center'
                            }}
                        >
                            <Grid item xs={12}>
                                <Grid container spacing={gridSpacing}>
                                    {metrics.metricRange?.map((metric: any, index: number) => (
                                        <Grid item xs={4} key={`metricRange-${index}`}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        {intl.formatMessage({ id: metric?.typeDescription })}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="h5">{`${metric.percentage.toFixed(2)} %`}</Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Grid>
            </Grid>
        </Card>
    );
};

export default SideIconCard;

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}
